import Phaser from 'phaser';
import { CampaignState } from '../state/CampaignState';

export interface MemorialSceneData {
  /** Where to go after the memorial: 'briefing' (default) */
  nextScene?: string;
  /** Whether to reset the roster before continuing (after full wipe). */
  resetRoster?: boolean;
}

export class MemorialScene extends Phaser.Scene {
  constructor() {
    super({ key: 'MemorialScene' });
  }

  create(data?: MemorialSceneData): void {
    const { width, height } = this.scale;
    const nextScene = data?.nextScene ?? 'BriefingScene';
    const resetRoster = data?.resetRoster ?? false;

    if (resetRoster) {
      CampaignState.reset();
    }

    this.cameras.main.fadeIn(700, 0, 0, 0);

    // Sombre dark background
    this.add.rectangle(0, 0, width, height, 0x080808).setOrigin(0, 0);

    // Thin grey stripes
    this.add.rectangle(0, 0, width, 4, 0x444444).setOrigin(0, 0);
    this.add.rectangle(0, height - 4, width, 4, 0x444444).setOrigin(0, 0);

    // Title
    this.add
      .text(width / 2, 36, '✝  IN MEMORIAM  ✝', {
        fontSize: '20px',
        color: '#bbbbbb',
        fontFamily: 'monospace',
        letterSpacing: 4,
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0);

    this.add.rectangle(width / 2, 80, 400, 1, 0x333333).setOrigin(0.5, 0);

    const fallen = CampaignState.fallen;

    if (fallen.length === 0) {
      // No casualties
      this.add
        .text(width / 2, height / 2, 'No soldiers were lost.', {
          fontSize: '16px',
          color: '#667766',
          fontFamily: 'monospace',
          resolution: window.devicePixelRatio || 1,
        })
        .setOrigin(0.5, 0.5);
    } else {
      // List fallen soldiers in two columns if needed
      const colCount = fallen.length > 8 ? 2 : 1;
      const perCol = Math.ceil(fallen.length / colCount);
      const startX = colCount === 2 ? width / 2 - 160 : width / 2 - 100;
      const startY = 100;
      const rowH = 30;

      for (let i = 0; i < fallen.length; i++) {
        const col = Math.floor(i / perCol);
        const row = i % perCol;
        const x = startX + col * 200;
        const y = startY + row * rowH;

        this.add
          .text(x, y, `✝  ${fallen[i].name}`, {
            fontSize: '15px',
            color: '#888888',
            fontFamily: 'monospace',
            resolution: window.devicePixelRatio || 1,
          })
          .setOrigin(0, 0);
      }
    }

    // "They will be remembered." tagline
    this.add
      .text(width / 2, height - 100, 'They will be remembered.', {
        fontSize: '13px',
        color: '#444444',
        fontFamily: 'monospace',
        fontStyle: 'italic',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0);

    // Continue button
    this.createButton(width / 2, height - 55, 'CONTINUE', 0x888888, 0x111111, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.cameras.main.once('camerafadeoutcomplete', () => {
        const living = CampaignState.getLiving();
        const nextMission = CampaignState.missionNumber;
        const levelKey = `level${Math.min(nextMission, 4)}`;
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const nextLevelData: any = (this.cache.tilemap.get(levelKey) as any)?.data;
        const meta: any = nextLevelData?.metadata;
        this.scene.start(nextScene, {
          missionNumber: nextMission,
          missionName: meta?.name ?? `Operation ${nextMission > 1 ? 'Deepstrike' : 'Freefall'}`,
          objectives: meta?.objectives?.map((o: { label: string }) => o.label)
            ?? ['Eliminate all enemies', 'Destroy enemy HQ'],
          troopCount: living.length,
          difficulty: nextLevelData?.difficulty ?? 'normal',
        });
      });
    });
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    textColor: number,
    bgColor: number,
    onClick: () => void
  ): void {
    const btnWidth = 180;
    const btnHeight = 40;

    const bg = this.add
      .rectangle(x, y, btnWidth, btnHeight, bgColor)
      .setStrokeStyle(2, textColor)
      .setInteractive({ useHandCursor: true });

    const text = this.add
      .text(x, y, label, {
        fontSize: '15px',
        color: `#${textColor.toString(16).padStart(6, '0')}`,
        fontFamily: 'monospace',
        resolution: window.devicePixelRatio || 1,
      })
      .setOrigin(0.5, 0.5);

    bg.on('pointerover', () => {
      bg.setFillStyle(textColor);
      text.setColor(`#${bgColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(bgColor);
      text.setColor(`#${textColor.toString(16).padStart(6, '0')}`);
    });
    bg.on('pointerdown', onClick);
  }
}
